/**
 * Intent v1 — coverage model.
 *
 * `computeCoverage` is the one place that decides whether an intent is complete.
 * Completion is a deterministic check over a slot set, not a model judgement:
 * every *required* slot must be filled, and every *optional* slot must be filled or
 * explicitly marked unknown. A slot that is absent entirely is never good enough.
 *
 * Every consumer — the API completion check, the web review gate, the `/spectree:bug-to-er`
 * branch, the draft-mode counter, `renderIntent`, and `assertIntentIsComplete` — must
 * call this rather than re-implementing the rule.
 *
 * ## The rule is unchanged; only its inputs are scoped
 *
 * The completion *rule* is exactly what it has always been. What changed is the slot
 * set it runs over: membership is now resolved per {@link IntentKind} from
 * `KIND_SLOT_SETS`, so a feature reporter is no longer held to reproduction steps and
 * a severity rating, and an inquiry is no longer held to either. Slots outside the
 * resolved kind are not merely optional — they do not apply, and never appear in any
 * of the returned lists.
 *
 * ## The kind defaults to `bug`
 *
 * The kind is resolved as: the explicit `kind` argument, then the intent's
 * `frontmatter.kind`, then `"bug"`. Bug is the default because it is exactly what this
 * function has always evaluated — before intent kinds existed there was one registry
 * and it was bug-shaped — so every document written before the kind existed keeps its
 * meaning, and an unrecognised kind value degrades to the reference behaviour rather
 * than throwing. The argument stays optional so the six call sites above compile
 * unchanged until they are migrated to pass their own session kind.
 */
import { INTENT_KINDS } from "./types.js";
import { requiredSlotIdsForKind, slotIdsForKind } from "./slots.js";
/** Whether `value` is one of the recognised {@link INTENT_KINDS}. */
function isIntentKind(value) {
    return typeof value === "string" && INTENT_KINDS.includes(value);
}
/**
 * Resolve the kind an intent should be evaluated against.
 *
 * Reads the frontmatter defensively — the field is optional on the wire and a document
 * written before kinds existed carries no kind at all — so an absent, malformed, or
 * unrecognised value falls back to `"bug"` rather than throwing.
 *
 * @param intent The intent whose frontmatter may carry a kind.
 * @param override An explicit kind from a caller holding session state; wins when given.
 * @returns The resolved kind, defaulting to `"bug"`.
 */
export function resolveIntentKind(intent, override) {
    if (override !== undefined)
        return override;
    const declared = intent.frontmatter.kind;
    return isIntentKind(declared) ? declared : "bug";
}
/**
 * Evaluate an intent's slot coverage for its kind.
 *
 * @param intent The intent to evaluate.
 * @param kind Optional explicit kind, overriding the intent's own frontmatter.
 * @returns Slot lists plus the completion verdict, scoped to the resolved kind.
 */
export function computeCoverage(intent, kind) {
    const filled = [];
    const unknown = [];
    const missing = [];
    const requiredRemaining = [];
    const optionalRemaining = [];
    // Membership comes from KIND_SLOT_SETS, never from the deprecated per-slot `required`
    // flag, so adding a slot to the registry for one kind cannot change another's verdict.
    const resolvedKind = resolveIntentKind(intent, kind);
    const applicable = slotIdsForKind(resolvedKind);
    const requiredIds = new Set(requiredSlotIdsForKind(resolvedKind));
    for (const id of applicable) {
        const value = intent.slots[id];
        const required = requiredIds.has(id);
        if (value === undefined) {
            missing.push(id);
            if (required) {
                requiredRemaining.push(id);
            }
            else {
                optionalRemaining.push(id);
            }
            continue;
        }
        if (value.state === "filled") {
            filled.push(id);
            continue;
        }
        unknown.push(id);
        // An explicit unknown satisfies an optional slot but never a required one.
        if (required) {
            requiredRemaining.push(id);
        }
    }
    return {
        kind: resolvedKind,
        filled,
        unknown,
        missing,
        requiredRemaining,
        optionalRemaining,
        isComplete: requiredRemaining.length === 0 && optionalRemaining.length === 0,
        totalSlots: applicable.length,
    };
}
/**
 * Number of slots without an answer — explicit unknowns plus slots never addressed.
 * This is the `N` in the agent-draft status header.
 *
 * Both inputs are already scoped to the coverage's kind, so this never counts a slot
 * that does not apply to the intent.
 */
export function countUnanswered(coverage) {
    return coverage.unknown.length + coverage.missing.length;
}
//# sourceMappingURL=coverage.js.map